<template>
  <NuxtLink v-if="shopAccess" to="/cart" class="cart-button" :title="hint">
    <el-badge :value="count" :hidden="count === 0" :max="99" type="danger">
      <el-icon :size="22"><ShoppingCart /></el-icon>
    </el-badge>
    <!-- Срок появляется, только когда его есть из чего посчитать:
         непустая корзина, выбранный город и ответивший СДЭК -->
    <span v-if="etaLabel" class="cart-button__eta">{{ etaLabel }}</span>
  </NuxtLink>
</template>

<script setup>
import { ShoppingCart } from "@element-plus/icons-vue";

const shopAccess = useShopAccess();
const { count } = useCart();
const { city } = useCity();

const eta = ref(null);
// Город и корзина приезжают порознь, и наблюдатель успевает сработать
// несколько раз подряд — без этой отметки на одну загрузку страницы уходило
// три одинаковых запроса
const lastKey = ref("");

// Считаем на сервере: там и корзина, и настройки СДЭК. Ответ калькулятора
// кешируется на полчаса, так что переходы по сайту в СДЭК не бьют
const load = async () => {
  if (!shopAccess.value || !city.value?.code || count.value === 0) {
    eta.value = null;
    lastKey.value = "";
    return;
  }

  const key = `${city.value.code}:${count.value}`;
  if (key === lastKey.value) return;
  lastKey.value = key;

  try {
    eta.value = await $fetch("/api/delivery/eta", {
      query: { city_code: city.value.code },
    });
  } catch {
    // Срок — необязательная подсказка, молчим
    eta.value = null;
    lastKey.value = "";
  }
};

// Меняется состав корзины или город — пересчитываем. immediate вместо
// onMounted: шапка живёт в макете и не перемонтируется при переходах, поэтому
// первый расчёт должен случиться сразу, как появятся город и товары.
// На сервере город всегда пуст (он в localStorage), и load выходит без запроса
watch([() => count.value, () => city.value?.code], load, { immediate: true });

const plural = (value, one, few, many) => {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = value % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

const etaLabel = computed(() => {
  if (!eta.value) return "";
  const { total_min, total_max } = eta.value;
  if (!total_max) return "";

  const range = total_min && total_min !== total_max ? `${total_min}–${total_max}` : `${total_max}`;
  return `${range} ${plural(total_max, "день", "дня", "дней")}`;
});

// Подсказка раскладывает срок на изготовление и перевозку — на самой кнопке
// для этого нет места
const hint = computed(() => {
  if (!eta.value) return "Корзина";

  const parts = ["Корзина"];
  if (eta.value.production_days > 0) {
    const d = eta.value.production_days;
    parts.push(`изготовление ${d} ${plural(d, "рабочий день", "рабочих дня", "рабочих дней")}`);
  }
  parts.push(`доставка ${eta.value.delivery_min}–${eta.value.delivery_max} дн. (${eta.value.tariff_name})`);
  parts.push(`город: ${city.value?.city ?? "не выбран"}`);

  return parts.join(" · ");
});
</script>

<style scoped>
.cart-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 6px;
  color: var(--el-text-color-primary);
  line-height: 1;
}

.cart-button:hover {
  color: var(--el-color-primary);
}

.cart-button__eta {
  font-size: 12px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}
</style>
